/* ─────────────────────────────────────────────────────────────
   The simulated data center behind the monitoring demo.

   Nothing here touches a network or a device. Every reading is a pure
   function of the clock, so the same instant always gives the same
   numbers and a test can pin one down.

   It is a model rather than a table of canned values because the screens
   have to agree with each other: a feeder's current is its power over its
   voltage, a bus is the sum of its feeders, neutral current comes from the
   three phase waveforms, a joint's loss from its current and resistance.
   When the load moves, everything downstream of it moves with it.

   Two clocks run through it. `ms` is now, and only drives the readings.
   `anchor` is when the page opened, and fixes when past events happened,
   so the log does not slide forward while someone is reading it.

   A scenario is a fault injected into the model, not a canned screen state:
   it changes the buses, and every reading, alarm and diagram follows from
   that. Alarms are worked out from the readings against THRESHOLDS, with their
   delays and reset points, so a value that crosses a limit raises one by itself.
   ───────────────────────────────────────────────────────────── */

export const TICK_MS = 2000;
const SAMPLE_MS = 5 * 60 * 1000;
const SAMPLES_24H = 288;
const MINUTE = 60 * 1000;

export const BUSES = ['A', 'B'];
export const PHASES = ['L1', 'L2', 'L3'];

/* Validated with the dataviz palette check against --bg-panel, including
   for colorblind readers: a phase or a bus keeps its color on every screen.
   The brand yellow stays the interface accent and is deliberately not a data
   color: it is far too light to sit in the band that passes on near-black. */
export const PHASE_COLOR = { L1: '#4C86E8', L2: '#B8891F', L3: '#C9559B' };
export const BUS_COLOR = { A: '#B8891F', B: '#2F90C8' };
export const NEUTRAL_COLOR = '#A1A1A6';

/* A repeatable stand-in for noise: the same inputs always give the same
   value in [-1, 1]. */
const noise = (a, b = 0) => {
    const x = Math.sin(a * 127.1 + b * 311.7) * 43758.5453;
    return (x - Math.floor(x)) * 2 - 1;
};

const round = (value, places = 2) => {
    const scale = 10 ** places;
    return Math.round(value * scale) / scale;
};

const sum = values => values.reduce((total, value) => total + value, 0);

const zone = { timeZone: 'America/New_York', hourCycle: 'h23' };
const clockFormat = new Intl.DateTimeFormat('en-US', { ...zone, hour: '2-digit', minute: '2-digit' });
const stampFormat = new Intl.DateTimeFormat('en-US', { ...zone, hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dateFormat = new Intl.DateTimeFormat('en-US', { ...zone, month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });

export const clock = ms => clockFormat.format(ms);
export const clockFull = ms => stampFormat.format(ms);
export const stamp = ms => `${stampFormat.format(ms)}.${String(Math.floor(ms % 1000)).padStart(3, '0')}`;
export const dateTime = ms => `${dateFormat.format(ms)} ET`;

/* ── The fixed design of the example system ─────────────────── */

export const SYSTEM = {
    busLimitA: 4000,
    feederLimitA: 800,
    nominalVdc: 800,
    moduleKw: 400,
    modules: 9,
    sections: 6
};

export const PROFILES = {
    '480': { id: '480', label: '480/277 V · 60 Hz', ll: 480, hz: 60 },
    '400': { id: '400', label: '400/230 V · 50 Hz', ll: 400, hz: 50 }
};

const BUS_SPEC = {
    A: {
        volts: 800.3, base: 925, seed: 1,
        shares: [150.7, 161.0, 155.8, 173.7, 139.5, 149.6],
        pathDrop: [0.7, 0.8, 0.6, 1.2, 0.7, 0.9],
        resistance: [25.0, 28.9, 26.7, 56.9, 23.4, 30.4],
        rise: [14.9, 17.3, 15.8, 35.1, 14.2, 18.0],
        ambient: 28.4,
        poleOffset: 10.85,
        insulation: 1.82, residual: 14.2, capacitance: 6.8,
        rippleV: 1.6, ripplePp: 8.4, rippleI: 3.8,
        efficiency: 97.5, soc: 94, soh: 98.4, usableKwh: 310.8,
        cells: [3.28, 3.31], batteryTemp: [26.1, 29.4],
        conductor: [44.2, 43.8]
    },
    B: {
        volts: 799.9, base: 922, seed: 2,
        shares: [148.2, 158.9, 157.7, 171.4, 141.6, 149.3],
        pathDrop: [0.6, 0.7, 0.8, 0.9, 0.7, 0.8],
        resistance: [22.7, 25.6, 25.3, 28.8, 25.4, 28.5],
        rise: [14.6, 16.1, 15.8, 19.2, 16.6, null],
        ambient: 28.1,
        poleOffset: -0.55,
        insulation: 2.36, residual: 11.6, capacitance: 6.3,
        rippleV: 1.3, ripplePp: 7.1, rippleI: 3.1,
        efficiency: 97.6, soc: 92, soh: 97.8, usableKwh: 308.0,
        cells: [3.27, 3.30], batteryTemp: [26.1, 29.2],
        conductor: [43.1, 42.8]
    }
};

/* ── The historian: 5-minute samples, trailing 24 hours ─────── */

const busPowerAt = (bus, ms) => {
    const spec = BUS_SPEC[bus];
    const sample = Math.floor(ms / SAMPLE_MS);
    const hour = ((sample * SAMPLE_MS) / 3600000) % 24;
    const daily = 0.04 * Math.sin((2 * Math.PI * (hour - 6)) / 24)
        + 0.016 * Math.sin((2 * Math.PI * hour) / 6);
    const drift = 0.005 * noise(sample, spec.seed) + 0.008 * noise(Math.floor(sample / 3), spec.seed + 7);

    return spec.base * (1 + daily + drift);
};

const busVoltsAt = (bus, ms) => {
    const spec = BUS_SPEC[bus];
    const sample = Math.floor(ms / SAMPLE_MS);

    return spec.volts + 0.45 * Math.sin(sample / 19 + spec.seed) + 0.18 * noise(sample, spec.seed + 3);
};

/* Sampled trailing history for one bus, oldest first. */
export const history = (bus, ms, count = SAMPLES_24H) => {
    const latest = Math.floor(ms / SAMPLE_MS) * SAMPLE_MS;
    const points = [];

    for (let i = count - 1; i >= 0; i -= 1) {
        const t = latest - i * SAMPLE_MS;
        const kw = busPowerAt(bus, t);
        const volts = busVoltsAt(bus, t);

        points.push({ t, kw, volts, amps: (kw * 1000) / volts });
    }

    return points;
};

const rolling15 = points => {
    const windows = points.map((_, i) => {
        const slice = points.slice(Math.max(0, i - 2), i + 1);
        return sum(slice.map(p => p.kw)) / slice.length;
    });

    return { now: windows[windows.length - 1], peak: Math.max(...windows) };
};

const energyOf = points => sum(points.map(p => (p.kw * SAMPLE_MS) / 3600000));

/* One-hour imported-energy windows for the bar chart, oldest first. */
export const hourlyEnergy = (bus, ms) => {
    const points = history(bus, ms);
    const hours = [];

    for (let i = 0; i < 24; i += 1) {
        const slice = points.slice(i * 12, (i + 1) * 12);
        hours.push({ t: slice[slice.length - 1].t, kwh: energyOf(slice) });
    }

    return hours;
};

/* ── Scenarios ──────────────────────────────────────────────── */

export const SCENARIOS = [
    {
        id: 'joint-overheat',
        title: 'Joint overheating',
        where: 'Bus A · Joint J04-A',
        text: 'The connection that is already running warm keeps heating until it crosses the 75 °C critical limit. Its estimated resistance climbs with it.',
        watch: ['thermal', 'protection', 'overview']
    },
    {
        id: 'utility-outage',
        title: 'Utility outage',
        where: 'Path A',
        text: 'The supply to path A is lost. Battery A carries Bus A through its DC/DC interface, the UPS output holds, and the runtime counts down. If the battery runs out, path B picks up the load.',
        watch: ['site', 'ac', 'energy', 'events']
    },
    {
        id: 'path-loss',
        title: 'Loss of a UPS path',
        where: 'Path A',
        text: 'The Bus A main opens and path A stops. Path B carries the whole load, redundancy is gone, and the joints on Bus B start to warm under the extra current.',
        watch: ['site', 'overview', 'thermal', 'protection']
    },
    {
        id: 'insulation-fault',
        title: 'Insulation fault',
        where: 'Bus B',
        text: 'Insulation resistance on the floating bus collapses and residual current climbs, while pole-to-pole voltage stays normal. Pole-to-earth voltage shows the shift.',
        watch: ['protection', 'electrical']
    },
    {
        id: 'module-failure',
        title: 'Rectifier module failure',
        where: 'Rectifier A · Module M04',
        text: 'One rectifier module fails. Path A keeps running on the rest, but N+1 capacity and surviving-path margin both shrink, and current sharing widens.',
        watch: ['energy', 'site', 'overview']
    }
];

/* Battery discharge runs this much faster than real time, so a 20-minute
   runtime plays out in about three minutes and can be watched to the end. */
export const TIME_COMPRESSION = 6;

const stateOf = (scenario, ms) => ({
    id: scenario ? scenario.id : null,
    startedAt: scenario ? scenario.startedAt : null,
    elapsed: scenario ? Math.max(0, (ms - scenario.startedAt) / 1000) : 0
});

const ramp = (state, seconds) => Math.min(1, state.elapsed / seconds);

const modeOf = (bus, state, ms) => {
    if (bus !== 'A') return 'normal';
    if (state.id === 'path-loss') return 'down';

    if (state.id === 'utility-outage') {
        const used = busPowerAt('A', ms) * ((state.elapsed * TIME_COMPRESSION) / 3600);
        return used >= BUS_SPEC.A.usableKwh ? 'down' : 'battery';
    }

    return 'normal';
};

/* ── The UPS DC side ────────────────────────────────────────── */

/* How long a joint takes to settle at a new current, in simulated seconds. */
const THERMAL_LAG = 90;

const feederOf = (bus, index, busVolts, targetKw, normalKw, ms, state, mode) => {
    const spec = BUS_SPEC[bus];
    const tick = Math.floor(ms / TICK_MS);
    const section = String(index + 1).padStart(2, '0');
    const share = spec.shares[index] / sum(spec.shares);
    const wiggle = 1 + 0.0015 * noise(tick, index + spec.seed * 10);
    const down = mode === 'down';

    const kw = down ? 0 : targetKw * share * wiggle;
    const volts = down ? 0 : busVolts - spec.pathDrop[index];
    const amps = down ? 0 : (kw * 1000) / volts;
    const refVolts = spec.volts - spec.pathDrop[index];
    const refAmps = (spec.shares[index] * 1000) / refVolts;
    const normalAmps = (normalKw * share * wiggle * 1000) / refVolts;

    const baseRise = spec.rise[index];
    const stale = baseRise === null;
    const hot = bus === 'A' && index === 3;
    const overheating = hot && state.id === 'joint-overheat';

    let rise = null;
    let slope = null;

    if (!stale) {
        const settled = baseRise * (normalAmps / refAmps) ** 0.9;
        const target = baseRise * (amps / refAmps) ** 0.9;
        const decay = Math.exp(-state.elapsed / THERMAL_LAG);
        const shift = state.id ? target - settled : 0;

        rise = settled + shift * (1 - decay) + 0.05 * noise(tick, index + 40);
        slope = (hot ? 0.145 + 0.015 * noise(Math.floor(ms / MINUTE)) : 0.02) + (shift / THERMAL_LAG) * decay * 60;

        if (overheating) {
            rise += 17 * ramp(state, 80);
            if (state.elapsed < 80) slope += (17 / 80) * 60;
        }
    }

    const resistance = spec.resistance[index] + (overheating ? 40 * ramp(state, 80) : 0);

    return {
        id: `DC-${bus}${section}`,
        short: `${bus}${index + 1}`,
        inverter: `INV-${bus}${section}`,
        joint: `J${section}-${bus}`,
        bus,
        index,
        location: `UPS inverter section ${section}`,
        volts,
        amps,
        kw,
        loading: (amps / SYSTEM.feederLimitA) * 100,
        temp: stale ? null : spec.ambient + rise,
        rise,
        ambient: spec.ambient,
        slope,
        stale,
        breaker: down ? 'Open' : 'Closed',
        /* The estimate is only valid above 50 A at a stable load. */
        rUOhm: amps >= 50 ? resistance : null,
        dropMv: (resistance * amps) / 1000,
        lossW: (amps * amps * resistance) / 1e6,
        pathDropV: down ? 0 : spec.pathDrop[index],
        pathDropPct: down ? 0 : (spec.pathDrop[index] / busVolts) * 100
    };
};

const THERMAL_ADVISORY_C = 60;
const THERMAL_CRITICAL_C = 75;

/* Minutes until this joint reaches the critical limit at its present rate of
   rise, or null if it is not heading there. */
export const timeToCritical = feeder => {
    if (feeder.stale || feeder.temp >= THERMAL_CRITICAL_C || feeder.slope < 0.05) return null;
    return (THERMAL_CRITICAL_C - feeder.temp) / feeder.slope;
};

const busOf = (bus, ms, state, modes) => {
    const spec = BUS_SPEC[bus];
    const tick = Math.floor(ms / TICK_MS);
    const points = history(bus, ms);
    const mode = modes[bus];
    const partner = bus === 'A' ? 'B' : 'A';
    const down = mode === 'down';

    const normalKw = busPowerAt(bus, ms) * (1 + 0.002 * noise(tick, spec.seed));
    const carried = modes[partner] === 'down' ? busPowerAt(partner, ms) : 0;
    const targetKw = down ? 0 : normalKw + carried;

    let volts = busVoltsAt(bus, ms) + 0.05 * noise(tick, spec.seed + 5);
    if (down) volts = 0;
    else if (mode === 'battery') volts = 782 + 0.6 * noise(tick, spec.seed + 6);
    else if (carried) volts -= 1.6;

    const feeders = spec.shares.map((_, i) => feederOf(bus, i, volts, targetKw, normalKw, ms, state, mode));

    const amps = sum(feeders.map(f => f.amps));
    const kw = (volts * amps) / 1000;
    const feederKw = sum(feeders.map(f => f.kw));
    const demand = rolling15(points);
    const byVolts = [...points].sort((a, b) => a.volts - b.volts);
    const byAmps = [...points].sort((a, b) => a.amps - b.amps);

    const fault = bus === 'B' && state.id === 'insulation-fault' ? ramp(state, 40) : 0;
    const insulation = spec.insulation + (0.38 - spec.insulation) * fault;
    const residual = spec.residual + (36 - spec.residual) * fault;
    const offset = spec.poleOffset + 180 * fault;
    const positive = down ? 0 : volts / 2 + offset;

    const failed = bus === 'A' && state.id === 'module-failure' && state.elapsed >= 4;
    const modulesOnline = SYSTEM.modules - (failed ? 1 : 0);
    const moduleKw = (modulesOnline - 1) * SYSTEM.moduleKw;

    const rippleV = down ? 0 : spec.rippleV;
    const rippleI = down ? 0 : spec.rippleI;

    const usedKwh = mode === 'battery' ? kw * ((state.elapsed * TIME_COMPRESSION) / 3600) : 0;
    const usableKwh = Math.max(0, spec.usableKwh - (bus === 'A' && state.id === 'utility-outage' && down ? spec.usableKwh : usedKwh));
    const remaining = usableKwh / spec.usableKwh;

    return {
        id: bus,
        mode,
        mainClosed: !down,
        volts,
        amps,
        kw,
        feeders,
        feederKw,
        lossKw: kw - feederKw,
        loading: (amps / SYSTEM.busLimitA) * 100,
        positivePe: positive,
        negativePe: down ? 0 : positive - volts,
        deviation: ((volts - SYSTEM.nominalVdc) / SYSTEM.nominalVdc) * 100,
        minVolts: byVolts[0],
        maxVolts: byVolts[byVolts.length - 1],
        peakAmps: byAmps[byAmps.length - 1],
        totalRmsA: Math.sqrt(amps ** 2 + rippleI ** 2),
        demandKw: demand.now,
        peakDemandKw: demand.peak,
        importKwh: energyOf(points),
        ripple: {
            volts: rippleV,
            peakToPeak: down ? 0 : spec.ripplePp,
            voltsPct: volts > 0 ? (rippleV / volts) * 100 : 0,
            amps: rippleI,
            ampsPct: amps > 0 ? (rippleI / amps) * 100 : 0
        },
        insulation,
        residual,
        capacitance: spec.capacitance,
        ambient: spec.ambient,
        conductor: spec.conductor,
        rectifier: {
            installedKw: SYSTEM.modules * SYSTEM.moduleKw,
            online: modulesOnline,
            failedModule: failed ? 'M04' : null,
            outputKw: mode === 'normal' ? kw : 0,
            nPlusOneKw: moduleKw,
            limitKw: Math.min(moduleKw, (volts * SYSTEM.busLimitA) / 1000),
            efficiency: spec.efficiency,
            sharing: failed ? 3.6 : 1.4,
            state: { normal: 'Online', battery: 'No AC input', down: 'Stopped' }[mode]
        },
        battery: {
            state: mode === 'battery' ? 'Discharging' : 'Standby',
            powerKw: mode === 'battery' ? kw : 0,
            soc: 10 + (spec.soc - 10) * remaining,
            soh: spec.soh,
            usableKwh,
            limitMw: 3.2,
            cells: [spec.cells[0] - 0.14 * (1 - remaining), spec.cells[1] - 0.14 * (1 - remaining)],
            temps: spec.batteryTemp,
            runtimeNow: kw > 0 ? (usableKwh / kw) * 60 : null
        }
    };
};

/* The alarm engine: every condition, worked out from the readings against
   THRESHOLDS, with critical ones first. `active` is what is already raised, and
   gives each limit its hysteresis: a reading has to fall back past the reset
   point to clear, so one hovering at a limit does not chatter. The delays are
   applied by applyAlarmDelays, since they need a memory of when a limit was
   first crossed. */
const conditionsOf = (buses, anchor, state, active) => {
    const found = [];
    const since = state.startedAt === null ? anchor : state.startedAt;
    const over = (id, value, limit, reset) => value >= (active.has(id) ? reset : limit);
    const under = (id, value, limit, reset) => value <= (active.has(id) ? reset : limit);

    const alarm = (fields) => ({ severity: 'advisory', kind: 'electrical', feeder: null, at: since, ...fields });
    const raise = (fields) => found.push(alarm(fields));

    BUSES.forEach(bus => {
        const data = buses[bus];

        data.feeders.forEach(feeder => {
            const section = String(feeder.index + 1).padStart(2, '0');

            if (feeder.stale) {
                raise({
                    id: feeder.joint,
                    bus,
                    asset: feeder.joint,
                    feeder: feeder.id,
                    kind: 'stale',
                    title: 'Temperature sensor is not reporting',
                    short: 'Temperature unavailable',
                    summary: 'Last sample over 5 minutes old',
                    body: `The last valid ${feeder.joint} temperature sample is more than 5 minutes old. Current, voltage and breaker telemetry remain available. Thermal condition is unknown until communication is restored.`,
                    where: `${feeder.joint} · Inverter ${section} · Temperature channel`,
                    at: anchor - 10 * MINUTE
                });
                return;
            }

            const criticalId = `${feeder.joint}-critical`;
            const advisory = alarm({
                delay: 30,
                id: feeder.joint,
                bus,
                asset: feeder.joint,
                feeder: feeder.id,
                kind: 'thermal',
                title: 'Elevated connection temperature',
                short: 'Elevated temperature',
                summary: feeder.temp >= THERMAL_ADVISORY_C
                    ? `${feeder.temp.toFixed(1)} °C, above the ${THERMAL_ADVISORY_C} °C advisory limit`
                    : `${feeder.temp.toFixed(1)} °C, holding until it falls below the 55 °C reset`,
                body: `Joint ${feeder.joint} is ${feeder.temp.toFixed(1)} °C, with a ${feeder.rise.toFixed(1)} K rise above local ambient. Review loading and the connection trend, and arrange inspection under the site’s electrical work procedures.`,
                where: `${feeder.joint} · Inverter ${section} · Positive DC tap-off`,
                at: feeder.joint === 'J04-A' ? anchor - 22 * MINUTE : since
            });

            if (over(criticalId, feeder.temp, THERMAL_CRITICAL_C, 70)) {
                raise({
                    delay: 10,
                    meanwhile: advisory,
                    id: criticalId,
                    bus,
                    asset: feeder.joint,
                    feeder: feeder.id,
                    severity: 'critical',
                    kind: 'thermal',
                    title: 'Connection temperature critical',
                    short: 'Critical temperature',
                    summary: feeder.temp >= THERMAL_CRITICAL_C
                        ? `${feeder.temp.toFixed(1)} °C, above the ${THERMAL_CRITICAL_C} °C critical limit`
                        : `${feeder.temp.toFixed(1)} °C, holding until it falls below the 70 °C reset`,
                    body: `Joint ${feeder.joint} is ${feeder.temp.toFixed(1)} °C, with a ${feeder.rise.toFixed(1)} K rise above local ambient. Reduce load on this feeder where the site allows, and arrange urgent inspection under the site’s electrical work procedures.`,
                    where: `${feeder.joint} · Inverter ${section} · Positive DC tap-off`
                });
            } else if (over(active.has(criticalId) ? criticalId : feeder.joint, feeder.temp, THERMAL_ADVISORY_C, 55)) {
                found.push(advisory);
            }
        });

        if (data.mode === 'down') {
            raise({
                id: `BUS-${bus}-DOWN`,
                bus,
                asset: `Bus ${bus}`,
                severity: 'critical',
                title: `Bus ${bus} is out of service`,
                short: 'Bus out of service',
                summary: `Main open · The other path carries the load, with no redundancy left`,
                body: `The Bus ${bus} main is open and path ${bus} is carrying no load. The whole protected load is on the surviving path, so another failure there would drop it. Restore the path under the site’s procedures.`,
                where: `Bus ${bus} · Main breaker`
            });
        }

        if (data.mode === 'battery') {
            raise({
                id: `BUS-${bus}-BATTERY`,
                bus,
                asset: `Battery ${bus}`,
                severity: 'critical',
                title: `Path ${bus} is on battery`,
                short: 'On battery',
                summary: `${data.battery.runtimeNow.toFixed(1)} min estimated runtime at present load`,
                body: `The AC supply to path ${bus} is lost and Battery ${bus} is carrying Bus ${bus}. Estimated runtime is ${data.battery.runtimeNow.toFixed(1)} minutes at the present load. The UPS output is being maintained.`,
                where: `Battery ${bus} · DC/DC interface`
            });
        }

        if (under(`IMD-${bus}`, data.insulation, 0.5, 0.6)) {
            raise({
                delay: 10,
                id: `IMD-${bus}`,
                bus,
                asset: `Bus ${bus}`,
                severity: 'critical',
                title: 'Insulation resistance below limit',
                short: 'Insulation fault',
                summary: `${data.insulation.toFixed(2)} MΩ, below the 0.50 MΩ limit`,
                body: `System insulation on Bus ${bus} is ${data.insulation.toFixed(2)} MΩ. The floating system keeps running through a first fault, but a second one could not be ridden through. Locate and clear it under the site’s procedures.`,
                where: `Bus ${bus} · Insulation monitor`
            });
        }

        if (over(`RCM-${bus}`, data.residual, 30, 25)) {
            raise({
                delay: 10,
                id: `RCM-${bus}`,
                bus,
                asset: `Bus ${bus}`,
                title: 'Residual current above limit',
                short: 'Residual current',
                summary: `${data.residual.toFixed(1)} mA, above the 30 mA limit`,
                body: `Residual current on Bus ${bus} is ${data.residual.toFixed(1)} mA AC+DC RMS. On a floating system this complements the insulation monitor and does not locate the fault by itself.`,
                where: `Bus ${bus} · Residual-current sensor`
            });
        }

        if (data.mode !== 'down' && under(`UV-${bus}`, data.volts, 760, 770)) {
            raise({
                delay: 0.5,
                id: `UV-${bus}`, bus, asset: `Bus ${bus}`, severity: 'critical',
                title: 'Bus undervoltage', short: 'Undervoltage',
                summary: `${data.volts.toFixed(1)} VDC, below the 760 VDC limit`,
                body: `Bus ${bus} is at ${data.volts.toFixed(1)} VDC, below the 760 VDC limit.`,
                where: `Bus ${bus} · DC meter`
            });
        }

        if (over(`OV-${bus}`, data.volts, 840, 830)) {
            raise({
                delay: 0.5,
                id: `OV-${bus}`, bus, asset: `Bus ${bus}`, severity: 'critical',
                title: 'Bus overvoltage', short: 'Overvoltage',
                summary: `${data.volts.toFixed(1)} VDC, above the 840 VDC limit`,
                body: `Bus ${bus} is at ${data.volts.toFixed(1)} VDC, above the 840 VDC limit.`,
                where: `Bus ${bus} · DC meter`
            });
        }

        if (over(`LOAD-${bus}`, data.loading, 80, 75)) {
            raise({
                delay: 60,
                id: `LOAD-${bus}`, bus, asset: `Bus ${bus}`,
                title: 'Bus loading above limit', short: 'High loading',
                summary: `${data.loading.toFixed(1)}%, above the 80% limit`,
                body: `Bus ${bus} is at ${data.loading.toFixed(1)}% of its continuous limit, above the 80% monitoring limit.`,
                where: `Bus ${bus} · DC meter`
            });
        }

        if (data.rectifier.failedModule) {
            raise({
                id: `RECT-${bus}-${data.rectifier.failedModule}`,
                bus,
                asset: `Rectifier ${bus}`,
                title: `Rectifier module ${data.rectifier.failedModule} failed`,
                short: 'Module failed',
                summary: `${data.rectifier.online}/${SYSTEM.modules} online · ${(data.rectifier.nPlusOneKw / 1000).toFixed(1)} MW N+1 capacity`,
                body: `Module ${data.rectifier.failedModule} on Rectifier ${bus} has failed. The path is still running, but N+1 capacity is down to ${(data.rectifier.nPlusOneKw / 1000).toFixed(1)} MW. Schedule the replacement.`,
                where: `Rectifier ${bus} · Module ${data.rectifier.failedModule}`
            });
        }
    });

    return [...found.filter(c => c.severity === 'critical'), ...found.filter(c => c.severity !== 'critical')];
};

/* Everything the DC screens read, for one instant. */
export const dcSnapshot = (ms, anchor = ms, scenario = null, active = new Set()) => {
    const state = stateOf(scenario, ms);
    const modes = { A: modeOf('A', state, ms), B: modeOf('B', state, ms) };
    const buses = { A: busOf('A', ms, state, modes), B: busOf('B', ms, state, modes) };
    const totalKw = buses.A.kw + buses.B.kw;
    const feederKw = buses.A.feederKw + buses.B.feederKw;

    /* What one path can carry is the lower of its rectifier capacity and its
       voltage at the current limit, so a failed module counts. */
    const live = BUSES.filter(bus => buses[bus].mode !== 'down');
    const capacityKw = Math.min(...live.map(bus => Math.min((buses[bus].volts * SYSTEM.busLimitA) / 1000, buses[bus].rectifier.limitKw)));

    const pairLoading = buses.A.feeders.map((feeder, i) => {
        const paired = buses.B.feeders[i];
        const volts = Math.min(feeder.volts, paired.volts) || Math.max(feeder.volts, paired.volts);

        return volts > 0 ? (((feeder.kw + paired.kw) * 1000) / volts / SYSTEM.feederLimitA) * 100 : 0;
    });

    return {
        ms,
        scenario: state,
        buses,
        feeders: BUSES.flatMap(bus => buses[bus].feeders),
        totalKw,
        feederKw,
        lossKw: totalKw - feederKw,
        efficiency: (feederKw / totalKw) * 100,
        demandKw: buses.A.demandKw + buses.B.demandKw,
        importKwh: buses.A.importKwh + buses.B.importKwh,
        capacityKw,
        survivingUse: (totalKw / capacityKw) * 100,
        headroomKw: capacityKw - totalKw,
        redundant: live.length === BUSES.length && buses.A.mode === 'normal',
        pairLoading,
        conditions: conditionsOf(buses, anchor, state, active)
    };
};

/* A condition only becomes an alarm once its limit has been crossed for that
   limit's delay. A critical one waiting out its delay leaves the advisory
   standing, so a hot joint is never without an alarm on the way up or down.
   What is already wrong when the page opens counts as having been wrong for a
   while. `memory` is kept by the caller between readings. */
export const applyAlarmDelays = (conditions, memory, ms) => {
    const { seen } = memory;
    const breached = conditions.flatMap(condition => (condition.meanwhile ? [condition, condition.meanwhile] : [condition]));
    const ids = new Set(breached.map(condition => condition.id));

    [...seen.keys()].forEach(id => {
        if (!ids.has(id)) seen.delete(id);
    });

    breached.forEach(condition => {
        if (!seen.has(condition.id)) seen.set(condition.id, memory.started ? ms : -Infinity);
    });

    memory.started = true;

    const due = condition => ms - seen.get(condition.id) >= (condition.delay || 0) * 1000;

    return conditions.flatMap(condition => {
        if (due(condition)) return [condition];
        return condition.meanwhile && due(condition.meanwhile) ? [condition.meanwhile] : [];
    });
};

/* What one feeder would carry if its partner on the other path were lost. */
export const feederDetail = (dc, id) => {
    const feeder = dc.feeders.find(f => f.id === id);
    if (!feeder) return null;

    const other = feeder.bus === 'A' ? 'B' : 'A';

    return {
        ...feeder,
        paired: dc.buses[other].feeders[feeder.index],
        afterLoss: dc.pairLoading[feeder.index],
        toCritical: timeToCritical(feeder),
        condition: dc.conditions.find(c => c.feeder === id) || null
    };
};

/* The historian's samples with the present reading on the end, so a trend
   always finishes where the figures beside it say it does. */
export const liveHistory = (bus, dc, count = SAMPLES_24H) => {
    const data = dc.buses[bus];

    return [...history(bus, dc.ms, count), { t: dc.ms, kw: data.kw, volts: data.volts, amps: data.amps }];
};

/* A feeder's load and joint temperature over the last hours, for its panel. */
export const feederTrend = (feeder, dc, count = 73) => {
    const spec = BUS_SPEC[feeder.bus];
    const share = spec.shares[feeder.index] / sum(spec.shares);
    const refAmps = (spec.shares[feeder.index] * 1000) / (spec.volts - spec.pathDrop[feeder.index]);
    const baseRise = spec.rise[feeder.index];

    const points = history(feeder.bus, dc.ms, count).map(point => {
        const amps = (point.kw * share * 1000) / (point.volts - spec.pathDrop[feeder.index]);

        return {
            t: point.t,
            amps,
            temp: baseRise === null ? null : spec.ambient + baseRise * (amps / refAmps) ** 0.9 + 0.2 * noise(point.t / SAMPLE_MS, feeder.index)
        };
    });

    return [...points, { t: dc.ms, amps: feeder.amps, temp: feeder.temp }];
};

export const inScope = (scope, bus) => scope === 'both' || scope === bus;

export const scopedConditions = (dc, scope) => dc.conditions.filter(c => inScope(scope, c.bus));

/* The channel count an operator would see for a scope: six joints and two
   conductor channels on each bus. */
export const thermalCoverage = (dc, scope) => {
    const buses = BUSES.filter(bus => inScope(scope, bus));
    const total = buses.length * 8;
    const stale = buses.flatMap(bus => dc.buses[bus].feeders).filter(f => f.stale).length;

    return { total, valid: total - stale, stale };
};

/* ── The AC side ────────────────────────────────────────────── */

export const METERS = {
    incomer: { id: 'incomer', code: 'INCOMER', label: 'Utility incomer', stage: 'AC supply', vFactor: 1.00292, dpf: 0.986, thdV: [1.7, 1.8, 1.6], thdI: [3.7, 3.9, 3.6], shares: [0.3363, 0.3321, 0.3316], spectrum: 'source' },
    rectifier: { id: 'rectifier', code: 'RECTIFIER', label: 'UPS rectifier input', stage: 'AC → DC', vFactor: 1.00104, dpf: 0.997, thdV: [1.8, 1.9, 1.7], thdI: [4.0, 4.2, 3.9], shares: [0.335, 0.3324, 0.3326], spectrum: 'source' },
    bypass: { id: 'bypass', code: 'BYPASS', label: 'Static bypass', stage: 'Standby', vFactor: 1.00188, dpf: null, thdV: [1.7, 1.8, 1.6], thdI: null, shares: [1 / 3, 1 / 3, 1 / 3], spectrum: 'source' },
    ups: { id: 'ups', code: 'UPS', label: 'UPS inverter output', stage: 'DC → AC', vFactor: 1, dpf: 0.986, thdV: [1.1, 1.2, 1.0], thdI: [19.0, 21.2, 18.4], shares: [0.3423, 0.3266, 0.3311], spectrum: 'load' },
    pdu: { id: 'pdu', code: 'PDU', label: 'PDU / rack feed', stage: 'AC loads', vFactor: 0.99542, dpf: 0.986, thdV: [2.6, 2.8, 2.4], thdI: [19.0, 21.2, 18.4], shares: [0.3423, 0.3266, 0.3311], spectrum: 'load' }
};

export const METER_ORDER = ['incomer', 'rectifier', 'bypass', 'ups', 'pdu'];

/* Other loads fed from the incomer ahead of the UPS: the difference between
   what the utility delivers and what reaches the rectifier. */
const OTHER_LOADS_KW = 50;
const INVERTER_EFFICIENCY = 0.9713;
const PDU_EFFICIENCY = 0.988;

/* Path B runs slightly cleaner than path A in the scenario. */
const PATH_THD = { A: { v: 1, i: 1 }, B: { v: 0.92, i: 0.87 } };

const VOLTAGE_SHAPE = { 2: 0.0244, 3: 0.1057, 5: 0.813, 7: 0.504, 9: 0.0488, 11: 0.2032, 13: 0.1464, 17: 0.0651, 19: 0.0488, 23: 0.0325, 25: 0.0244 };
const SOURCE_SHAPE = { 2: 0.0155, 3: 0.0622, 5: 0.777, 7: 0.544, 9: 0.0389, 11: 0.225, 13: 0.171, 17: 0.08, 19: 0.06, 23: 0.04, 25: 0.03 };
const LOAD_SHAPE = { 2: 0.01, 3: 0.55, 5: 0.6, 7: 0.42, 9: 0.22, 11: 0.17, 13: 0.12, 15: 0.08, 17: 0.06, 19: 0.05, 21: 0.03, 23: 0.03, 25: 0.02 };

export const ORDERS = Array.from({ length: 49 }, (_, i) => i + 2);

/* Scaled so the root-sum-square of the shape is exactly 1, which is what makes
   a spectrum add back up to the THD printed beside it. */
const normalized = shape => {
    const size = Math.sqrt(sum(Object.values(shape).map(v => v * v)));
    return Object.fromEntries(Object.entries(shape).map(([h, v]) => [h, v / size]));
};

const NORMAL = {
    voltage: normalized(VOLTAGE_SHAPE),
    source: normalized(SOURCE_SHAPE),
    load: normalized(LOAD_SHAPE)
};

export const orderGroup = h => {
    if (h % 3 === 0) return 'Triplen';
    return h % 2 === 0 ? 'Even order' : 'Odd order';
};

const PHASE_ANGLE = [0, -119.9, 120.1];
const PHASE_V = [1.0012, 0.999, 0.9994];
const DEG = Math.PI / 180;

const toRect = (mag, deg) => [mag * Math.cos(deg * DEG), mag * Math.sin(deg * DEG)];

/* Negative over positive sequence, from three fundamental phasors. */
const negativeSequence = phasors => {
    const rotate = ([x, y], deg) => {
        const [c, s] = [Math.cos(deg * DEG), Math.sin(deg * DEG)];
        return [x * c - y * s, x * s + y * c];
    };
    const combine = (a, b) => {
        const parts = [phasors[0], rotate(phasors[1], a), rotate(phasors[2], b)];
        return Math.hypot(sum(parts.map(p => p[0])) / 3, sum(parts.map(p => p[1])) / 3);
    };
    const positive = combine(120, 240);

    return positive > 0 ? (combine(240, 120) / positive) * 100 : null;
};

const SAMPLES_PER_WINDOW = 400;

/* Two cycles of every phase, built from the fundamental and its harmonics. */
const synthesize = (phases, nominalHz) => {
    const window = 2 / nominalHz;
    const time = [];
    const v = [[], [], []];
    const i = [[], [], []];
    const n = [];

    for (let s = 0; s < SAMPLES_PER_WINDOW; s += 1) {
        const t = (s / SAMPLES_PER_WINDOW) * window;
        const w = 2 * Math.PI * nominalHz * t;
        let neutral = 0;

        time.push(t * 1000);

        phases.forEach((phase, p) => {
            const vTheta = w + phase.vAngle * DEG;
            const iTheta = w + phase.iAngle * DEG;
            let vNow = Math.sin(vTheta);
            let iNow = Math.sin(iTheta);

            Object.entries(phase.harmonicsV).forEach(([h, pct]) => {
                vNow += (pct / 100) * Math.sin(Number(h) * vTheta);
            });
            Object.entries(phase.harmonicsI).forEach(([h, pct]) => {
                iNow += (pct / 100) * Math.sin(Number(h) * iTheta);
            });

            const vValue = Math.SQRT2 * phase.v1 * vNow;
            const iValue = Math.SQRT2 * phase.i1 * iNow;

            v[p].push(vValue);
            i[p].push(iValue);
            neutral += iValue;
        });

        n.push(neutral);
    }

    return { time, v, i, n };
};

const rms = values => Math.sqrt(sum(values.map(x => x * x)) / values.length);

/* Which meters have lost their voltage, given the state of the path. */
const deadMeters = (bus, dc) => {
    const { mode } = dc.buses[bus];

    if (mode === 'battery') return ['incomer', 'rectifier', 'bypass'];
    if (mode === 'down') return dc.scenario.id === 'utility-outage' ? METER_ORDER : ['ups', 'pdu'];
    return [];
};

/* The chain of powers along one path, which the diagram reads whichever meter
   is selected. */
export const pathPowers = (bus, dc) => {
    const spec = BUS_SPEC[bus];
    const data = dc.buses[bus];
    const dead = deadMeters(bus, dc);
    const rectifier = data.mode === 'normal' ? data.kw / (spec.efficiency / 100) : 0;
    const ups = data.feederKw * INVERTER_EFFICIENCY;

    return {
        incomer: dead.includes('incomer') ? 0 : rectifier + OTHER_LOADS_KW,
        rectifier,
        bypass: 0,
        ups,
        pdu: ups * PDU_EFFICIENCY
    };
};

const meterData = (meterId, bus, profile, kw, ms, dead) => {
    const meter = METERS[meterId];
    const tick = Math.floor(ms / TICK_MS);
    const scale = profile.ll / 480;
    const thdScale = PATH_THD[bus];
    const nominalLn = profile.ll / Math.sqrt(3);
    const llAvg = profile.ll * (dead ? 0 : meter.vFactor);
    const energized = kw > 0 && !dead;

    const phases = PHASES.map((name, p) => {
        const thdV = dead ? 0 : meter.thdV[p] * thdScale.v;
        const thdI = energized && meter.thdI ? meter.thdI[p] * thdScale.i : null;
        const vRms = (llAvg / Math.sqrt(3)) * PHASE_V[p] * (1 + 0.0002 * noise(tick, p + 20));
        const v1 = vRms / Math.sqrt(1 + (thdV / 100) ** 2);
        const pKw = kw * meter.shares[p];
        const dpf = energized ? meter.dpf : null;

        const i1 = energized ? (pKw * 1000) / (v1 * dpf) : 0;
        const iRms = energized ? i1 * Math.sqrt(1 + (thdI / 100) ** 2) : 0;
        const sKva = (vRms * iRms) / 1000;
        const displacement = energized ? Math.acos(dpf) / DEG : 0;

        const harmonicsV = Object.fromEntries(
            Object.entries(NORMAL.voltage).map(([h, share]) => [h, thdV * share])
        );
        const harmonicsI = energized
            ? Object.fromEntries(Object.entries(NORMAL[meter.spectrum]).map(([h, share]) => [h, thdI * share]))
            : {};

        return {
            name,
            vRms,
            v1,
            iRms,
            i1,
            pKw,
            sKva,
            q1Kvar: energized ? (v1 * i1 * Math.sin(displacement * DEG)) / 1000 : 0,
            truePf: energized ? pKw / sKva : null,
            dpf,
            thdV,
            thdI,
            vAngle: PHASE_ANGLE[p],
            iAngle: PHASE_ANGLE[p] - displacement,
            harmonicsV,
            harmonicsI
        };
    });

    return { meter, phases, nominalLn, scale, energized };
};

/* Everything the AC screens read, for one meter at one instant. */
export const acSnapshot = (ms, path, meterId, profileId, dc) => {
    const profile = PROFILES[profileId];
    const tick = Math.floor(ms / TICK_MS);
    const powers = pathPowers(path, dc);
    const dead = deadMeters(path, dc);
    const isDead = dead.includes(meterId);
    const data = meterData(meterId, path, profile, powers[meterId], ms, isDead);
    const { phases, energized } = data;

    const wave = synthesize(phases, profile.hz);
    const totalKw = sum(phases.map(p => p.pKw));
    const totalKva = sum(phases.map(p => p.sKva));
    const totalQ = sum(phases.map(p => p.q1Kvar));
    const fundamentalKva = sum(phases.map(p => (p.v1 * p.i1) / 1000));
    const fundamentalKw = sum(phases.map(p => (p.v1 * p.i1 * Math.cos((p.vAngle - p.iAngle) * DEG)) / 1000));

    const lineVolts = [[0, 1], [1, 2], [2, 0]].map(([a, b]) =>
        rms(wave.v[a].map((value, s) => value - wave.v[b][s]))
    );
    const voltageSeq = negativeSequence(phases.map(p => toRect(p.v1, p.vAngle)));
    const currentSeq = energized ? negativeSequence(phases.map(p => toRect(p.i1, p.iAngle))) : null;

    const crest = energized
        ? Math.max(...wave.i.map(series => Math.max(...series.map(Math.abs)) / rms(series)))
        : null;
    const meanI = sum(phases.map(p => p.iRms)) / 3;
    const spread = energized ? Math.max(...phases.map(p => Math.abs(p.iRms - meanI))) / meanI * 100 : null;

    const third = phases.map(p => toRect(
        p.i1 * ((p.harmonicsI[3] || 0) / 100),
        3 * p.iAngle
    ));
    const neutralThird = Math.hypot(sum(third.map(a => a[0])), sum(third.map(a => a[1])));

    const ratio = dc.buses[path].kw > 0 ? powers[meterId] / dc.buses[path].kw : 0;
    const points = history(path, ms);
    const demand = rolling15(points);

    return {
        ms,
        path,
        profile,
        meter: data.meter,
        meterTag: `AC-${path}-${data.meter.code}`,
        phases,
        powers,
        wave,
        frequency: isDead ? 0 : round(profile.hz + 0.01 * Math.round(noise(tick, 3))),
        frequencyRange: [profile.hz - 0.02, profile.hz + 0.02],
        lineVolts,
        avgLineVolts: sum(lineVolts) / 3,
        voltageSeq,
        currentSeq,
        neutralA: energized ? rms(wave.n) : 0,
        neutralThird: energized ? neutralThird : 0,
        crest,
        spread,
        totalKw,
        totalKva,
        totalQ,
        totalPf: energized ? totalKw / totalKva : null,
        totalDpf: energized ? fundamentalKw / fundamentalKva : null,
        maxThdV: Math.max(...phases.map(p => p.thdV)),
        maxThdI: energized ? Math.max(...phases.map(p => p.thdI)) : null,
        pst: 0.3 - (path === 'B' ? 0.018 : 0),
        plt: 0.273 - (path === 'B' ? 0.016 : 0),
        importKwh: energyOf(points) * ratio,
        demandKw: demand.now * ratio,
        peakDemandKw: demand.peak * ratio,
        pathVolts: Object.fromEntries(METER_ORDER.map(id => [id, dead.includes(id) ? 0 : profile.ll * METERS[id].vFactor])),
        dead,
        isDead,
        mode: dc.buses[path].mode,
        batteryState: dc.buses[path].battery.state,
        dcVolts: dc.buses[path].volts,
        soc: dc.buses[path].battery.soc
    };
};

/* Percent of fundamental for one order: read off the shape for voltage or
   current, and zero for an order the shape does not carry. */
export const harmonicOf = (ac, phaseIndex, order, kind) => {
    const phase = ac.phases[phaseIndex];
    const table = kind === 'current' ? phase.harmonicsI : phase.harmonicsV;

    return table[order] || 0;
};

/* ── Power quality events ───────────────────────────────────── */

const EVENT_SPEC = [
    {
        key: 'sag', type: 'sag', minutes: 22, seconds: 12.418,
        title: 'Input sag · inverter output maintained',
        meter: 'rectifier',
        magnitude: 72, duration: 120, phases: ['L1', 'L2', 'L3'],
        output: 99.8, window: [-30, 160], scale: [60, 110],
        note: 'Simulated rectifier-input voltage reduced to 72% of nominal for 120 ms. The separately modeled inverter output remains at 99.8%. This illustrates correlation, not a tested ride-through guarantee.'
    },
    {
        key: 'swell', type: 'swell', minutes: 71, seconds: 33.905,
        title: 'Incomer swell',
        meter: 'incomer',
        magnitude: 112, duration: 25, phases: ['L1', 'L2', 'L3'],
        output: 100, window: [-6.3, 88.7], scale: [90, 125],
        note: 'Simulated incomer voltage rises to 112% of nominal for 25 ms while the inverter output remains close to nominal.'
    },
    {
        key: 'interruption', type: 'interruption', minutes: 147, seconds: 47.126,
        title: 'Bypass interruption · normal path unaffected',
        meter: 'bypass',
        magnitude: 3, duration: 80, phases: ['L1', 'L2', 'L3'],
        output: 100, window: [-30, 120], scale: [0, 110],
        note: 'Simulated interruption on the energized, unloaded static-bypass source. Normal double-conversion operation continues, and no transfer or switching command is modeled.'
    },
    {
        key: 'transient', type: 'transient', minutes: 233, seconds: 8.652,
        title: 'Incomer oscillatory transient',
        meter: 'incomer',
        magnitude: 148.1, duration: 0.8, phases: ['L1'],
        output: 100, window: [-4.2, 18.8], scale: [0, 155],
        note: 'Illustrative 6 kHz damped voltage transient on L1. The waveform shows instantaneous voltage, so this peak is not a residual-RMS percentage.'
    }
];

export const EVENT_TYPES = [
    { id: 'all', label: 'All events' },
    { id: 'sag', label: 'Sags' },
    { id: 'swell', label: 'Swells' },
    { id: 'interruption', label: 'Interruptions' },
    { id: 'transient', label: 'Transients' }
];

export const eventsFor = (path, anchor, state = null) => {
    const base = Math.floor(anchor / MINUTE) * MINUTE;

    const logged = EVENT_SPEC.map(spec => ({
        ...spec,
        id: `AC-${path}-EV-${spec.key.toUpperCase()}`,
        path,
        meterLabel: METERS[spec.meter].label,
        at: base - spec.minutes * MINUTE + spec.seconds * 1000
    }));

    /* A scenario writes to the same log, so the fault shows up where an
       operator would go looking for it. */
    if (state && path === 'A' && state.id === 'utility-outage') {
        return [{
            key: 'scenario',
            type: 'interruption',
            minutes: 0,
            id: 'AC-A-EV-OUTAGE',
            path,
            title: 'Utility interruption · UPS on battery',
            meter: 'incomer',
            meterLabel: METERS.incomer.label,
            magnitude: 0,
            duration: Math.max(1000, state.elapsed * 1000),
            phases: ['L1', 'L2', 'L3'],
            output: 99.8,
            window: [-30, 160],
            scale: [0, 110],
            note: 'Simulated loss of the utility supply to path A, still in progress. Battery A is carrying Bus A, and the inverter output stays near nominal.',
            at: state.startedAt
        }, ...logged];
    }

    return logged;
};

const envelopeAt = (event, t) => {
    if (event.type === 'transient') {
        return t >= 0 && t <= event.duration
            ? 100 + (event.magnitude - 100) * Math.sin((Math.PI * t) / event.duration)
            : 100;
    }

    const edge = Math.min(2, event.duration / 8);
    if (t < 0 || t > event.duration + edge) return 100;
    if (t < edge) return 100 + (event.magnitude - 100) * (t / edge);
    if (t > event.duration) return event.magnitude + (100 - event.magnitude) * ((t - event.duration) / edge);
    return event.magnitude;
};

/* The RMS envelope, upstream against the UPS output. */
export const eventEnvelope = event => {
    const [from, to] = event.window;
    const count = 160;
    const points = [];

    for (let s = 0; s <= count; s += 1) {
        const t = from + ((to - from) * s) / count;
        const inEvent = t >= 0 && t <= event.duration;

        points.push({
            t,
            upstream: envelopeAt(event, t),
            output: inEvent ? event.output : 100
        });
    }

    return points;
};

/* The waveform capture. The sample rate tracks the window so every capture
   holds the same number of points, which is what the charts draw. */
export const eventCapture = (event, profile) => {
    const [from, to] = event.window;
    const count = 900;
    const nominalPeak = (profile.ll / Math.sqrt(3)) * Math.SQRT2;
    const points = [];

    for (let s = 0; s < count; s += 1) {
        const t = from + ((to - from) * s) / count;
        const seconds = t / 1000;
        const row = { t };

        PHASES.forEach((name, p) => {
            const angle = 2 * Math.PI * profile.hz * seconds + PHASE_ANGLE[p] * DEG;
            let value;

            if (event.type === 'transient') {
                value = nominalPeak * Math.sin(angle);
                if (name === 'L1' && t >= 0) {
                    const ring = Math.exp(-t / 1.1) * Math.sin(2 * Math.PI * 6 * t);
                    value += nominalPeak * ((event.magnitude - 100) / 100) * 1.6 * ring;
                }
            } else {
                value = nominalPeak * (envelopeAt(event, t) / 100) * Math.sin(angle);
            }

            row[name] = value;
        });

        points.push(row);
    }

    return {
        points,
        windowMs: to - from,
        sampleRate: count / ((to - from) / 1000)
    };
};

/* The DC bus dip the electrical screen shows: a triggered capture, separate
   from anything the dashboard polls. Milliseconds from the trigger. */
export const fastCapture = {
    id: 'EVT-A-1042',
    location: 'Bus A input',
    minimum: 772.4,
    below780: 12.4,
    points: [
        [-10, 799.8], [-8, 800.2], [-6, 799.7], [-4, 800.1], [-2, 799.6], [-0.8, 799.9],
        [0, 788], [0.8, 776], [1.6, 772.4], [3, 773.4], [5, 773.9], [7, 773.5], [9, 773.9], [11, 774.5],
        [12.4, 780], [13.4, 794], [14.5, 803.4], [16, 800.9], [18, 800.4], [20, 800.2]
    ]
};

export const THRESHOLDS = [
    { label: 'Connection temperature', value: '≥ 60 °C', note: 'Advisory after 30 s · resets below 55 °C' },
    { label: 'Temperature critical', value: '≥ 75 °C', note: '10 s delay · resets below 70 °C' },
    { label: 'Thermal sample age', value: '> 30 s', note: 'Stale until a fresh valid sample arrives' },
    { label: 'Insulation resistance', value: '< 0.50 MΩ', note: 'Valid IMD result · 10 s delay · resets above 0.60 MΩ' },
    { label: 'Residual AC+DC RMS', value: '> 30 mA', note: 'DC–1 kHz · 10 s delay · resets below 25 mA' },
    { label: 'Bus undervoltage', value: '< 760 VDC', note: '500 ms delay · resets above 770 VDC' },
    { label: 'Bus overvoltage', value: '> 840 VDC', note: '500 ms delay · resets below 830 VDC' },
    { label: 'Bus loading', value: '> 80%', note: '60 s delay · resets below 75%' }
];

/* Past protection events, on the same anchored clock as the conditions. */
export const protectionHistory = anchor => [
    { at: anchor - 10 * MINUTE, asset: 'J06-B', event: 'Temperature sensor is not reporting', state: 'Active' },
    { at: anchor - 22 * MINUTE, asset: 'J04-A', event: 'Elevated connection temperature', state: 'Active' },
    { at: anchor - 59 * MINUTE, asset: 'Bus A', event: 'Captured voltage dip · 772.4 V · 12.4 ms', state: 'Recovered' },
    { at: anchor - 125 * MINUTE, asset: 'A + B', event: 'Insulation monitor self-test completed', state: 'Passed' }
];
